import React, { useCallback, useEffect } from "react";
import {
  Dialog,
  Stack,
  Typography,
  Link,
  useTheme,
  IconButton,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";

import { useDialog, useRequestState, useResponsive } from "@app/hooks";
import { FormProvider, RHFOtpVerify, useSnackbar } from "@app/components";
import { Strings } from "@app/constants";
import { useDispatch, useSelector } from "react-redux";
import { login, verifyOtp } from "../slice";
import { selectLoginState, selectVerifyOTPState } from "../selectors";
import { useNavigate } from "react-router-dom";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { selectIsAuthenticated } from "@app/modules/common";

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onClickOtpVerifications?: () => void;
  loginFormData?: any;
  onVerifyOtp?: (otp: string) => void;
  onResendOtp?: () => void;
  isLoading?: boolean;
}

const OtpVerificationsDialog = ({
  open,
  onClose,
  onClickOtpVerifications,
  loginFormData,
  onVerifyOtp,
  onResendOtp,
  isLoading = false,
}: LoginDialogProps) => {
  const isMobile = useResponsive("down", "md");
  const theme = useTheme();
  const methods = useForm();
  const navigate = useNavigate();
  const otpVerificationsDialog = useDialog();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const loginData = useSelector(selectLoginState);
  const verifyData = useSelector(selectVerifyOTPState);

  const handleSuccessVerify = useCallback(() => {
    if (onVerifyOtp) {
      return;
    }

    enqueueSnackbar(verifyData?.data?.message || "OTP verified successfully!", {
      variant: "success",
    });
    onClose?.();
    navigate(PATH_DASHBOARD.myProfile);
  }, [enqueueSnackbar, navigate, onClose, onVerifyOtp, verifyData?.data?.message]);

  const handleErrorVerify = useCallback(() => {
    if (onVerifyOtp) {
      return;
    }

    const errorMsg = Array.isArray(verifyData?.error?.message)
      ? verifyData?.error?.message[0]
      : verifyData?.error?.message || "Something went wrong";

    enqueueSnackbar(errorMsg, { variant: "error" });
  }, [enqueueSnackbar, onVerifyOtp, verifyData?.error?.message]);

  useRequestState({
    stateSelector: selectVerifyOTPState,
    errorShown: !onVerifyOtp,
    onFailed: handleErrorVerify,
    successMessageShown: !onVerifyOtp,
    onSuccess: handleSuccessVerify,
  });

  const textStyle = (size: number, weight = 400, color?: string) => ({
    fontFamily: "Work Sans",
    fontWeight: weight,
    fontSize: size,
    ...(color && { color }),
  });

  const onSubmit = (form: any) => {
    if (onVerifyOtp) {
      onVerifyOtp(form?.otp);
      return;
    }

    dispatch(verifyOtp(form?.otp));
  };

  const handleResendOTP = () => {
    if (onResendOtp) {
      onResendOtp();
      return;
    }

    dispatch(login(loginFormData));
  };

  useEffect(() => {
    if (onVerifyOtp) {
      return;
    }

    if (loginData?.status === 2 && loginData?.data) {
      enqueueSnackbar("OTP sent successfully!", { variant: "success" });
    } else if (loginData?.status === 3) {
      enqueueSnackbar(loginData?.error?.message?.[0] || "Login failed", {
        variant: "error",
      });
    }
  }, [enqueueSnackbar, loginData, onVerifyOtp]);

  // const isAuthenticated = useSelector(selectIsAuthenticated);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : "55%",
            maxWidth: isMobile ? "100%" : "90%",
            borderRadius: isMobile ? 0 : 3,
            p: isMobile ? 2 : 0,
          },
        }}
      >
        <Stack
          sx={{
            maxHeight: "100vh",
            overflowY: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          <Stack
            spacing={3}
            sx={{ position: "relative", p: isMobile ? 2 : 4, px: 4 }}
          >
            <IconButton
              onClick={onClose}
              sx={{ position: "absolute", top: 8, right: 8 }}
            >
              <Icon
                icon="material-symbols-light:close"
                width="24"
                height="24"
                color={theme.palette.common.black}
              />
            </IconButton>
            <Stack spacing={1} alignItems="center">
              <Typography sx={textStyle(isMobile ? 22 : 28, 700)}>
                {Strings.auth.signIn}
              </Typography>
              <Typography
                textAlign="center"
                sx={textStyle(isMobile ? 14 : 16, 400, theme.palette.grey[800])}
              >
                {Strings.authPopup.pleaseEnterOtp}
              </Typography>
            </Stack>

            <FormProvider methods={methods}>
              <Stack spacing={2} sx={{ px: 6.8 }}>
                <Stack sx={{}}>
                  <Typography
                    sx={[
                      textStyle(14, 500, theme.palette.grey[700]),
                      { textAlign: "center", mb: 2 },
                    ]}
                  >
                    {Strings.authPopup.otp}
                  </Typography>

                  <Stack spacing={2} sx={{ gap: 2 }} alignItems="center">
                    <RHFOtpVerify name="otp" numInputs={4} />
                  </Stack>
                </Stack>
                <Link
                  href="#"
                  underline="hover"
                  fontFamily="Work Sans"
                  fontWeight={500}
                  sx={{
                    mt: 1,
                    alignSelf: "center",
                    textDecoration: "underline",
                  }}
                  onClick={() => handleResendOTP()}
                >
                  {Strings.authPopup.reSendOtp}
                </Link>
              </Stack>

              <Stack alignItems="center">
                <LoadingButton
                  onClick={methods.handleSubmit(onSubmit)}
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "8px",
                    width: isMobile ? "100%" : "65%",
                    py: 1.5,
                    mt: 4,
                  }}
                  loading={isLoading}
                >
                  {Strings.auth.signIn}
                </LoadingButton>
              </Stack>
            </FormProvider>

            {onClickOtpVerifications && (
              <Stack alignItems="center">
                <Typography
                  sx={textStyle(
                    isMobile ? 13 : 15,
                    500,
                    theme.palette.grey[700],
                  )}
                >
                  {Strings.auth.alreadyHaveIn}
                  <Link
                    href="#"
                    underline="hover"
                    onClick={onClickOtpVerifications}
                    fontFamily="Work Sans"
                    fontWeight={500}
                    ml={1}
                  >
                    {Strings.authPopup.logIn}
                  </Link>
                </Typography>
              </Stack>
            )}

            <Typography
              textAlign="center"
              sx={[
                textStyle(isMobile ? 11 : 13, 500, theme.palette.grey[700]),
                { px: 4 },
              ]}
            >
              {Strings.authPopup.byContinuingYou}
            </Typography>
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
};

export { OtpVerificationsDialog };
